import { Form, Input, Radio } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomActionButtons,
  CustomInput,
  CustomModal,
  CustomSelect,
} from "sipli-fleet-core/components";
import {
  useMutationCreateWarehouseResource,
  useMutationUpdateWarehouseResource,
  useQueryGetWarehouseChildLookup,
  useQueryGetWarehouseLookup,
  useQueryGetWarehouseTypes,
} from "src/queries/warehouses.queries";
import type {
  LookupOption,
  WarehouseResource,
  WarehouseResourcePayload,
  WarehouseResourceType,
} from "src/services/warehouseService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import Notification from "src/util/libs/notification";
import { hierarchyConfig } from "../hierarchyConfig";
import { getLookupOptions, isResourceActive } from "../warehouseHierarchy.util";

interface WarehouseResourceModalProps {
  open: boolean;
  resource: WarehouseResourceType;
  record?: WarehouseResource | null;
  parentPayload?: Partial<WarehouseResourcePayload>;
  onClose: () => void;
}

interface ResourceFormValues {
  name: string;
  code?: string;
  description?: string;
  active: boolean;
  warehouseTypeId?: string | number;
  warehouseId?: string | number;
  areaId?: string | number;
  laneId?: string | number;
  shelfId?: string | number;
}

const formFieldConfig: Record<
  WarehouseResourceType,
  {
    codeLabelKey?: string;
    nameLabelKey: string;
    codePlaceholderKey?: string;
    namePlaceholderKey: string;
  }
> = {
  warehouse: {
    nameLabelKey: "warehouse_name",
    namePlaceholderKey: "enter_warehouse_name",
  },
  area: {
    codeLabelKey: "area_code",
    nameLabelKey: "area_name",
    codePlaceholderKey: "enter_area_code",
    namePlaceholderKey: "enter_area_name",
  },
  lane: {
    codeLabelKey: "lane_code",
    nameLabelKey: "lane_name",
    codePlaceholderKey: "enter_lane_code",
    namePlaceholderKey: "enter_lane_name",
  },
  shelf: {
    codeLabelKey: "shelf_code",
    nameLabelKey: "shelf_name",
    codePlaceholderKey: "enter_shelf_code",
    namePlaceholderKey: "enter_shelf_name",
  },
  position: {
    codeLabelKey: "code",
    nameLabelKey: "name",
    codePlaceholderKey: "enter_code",
    namePlaceholderKey: "enter_name",
  },
};

const getOptionalDistributorId = (value: unknown) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0
    ? parsedValue
    : undefined;
};

const getRelationId = (
  record: WarehouseResource | null | undefined,
  relationKey: "warehouse" | "area" | "lane" | "shelf",
  fallback?: string | number,
) => {
  const relation = record?.[relationKey];
  if (relation && typeof relation === "object" && "id" in relation) {
    return relation.id as string | number;
  }

  const directValue = record?.[`${relationKey}Id`];
  return typeof directValue === "string" || typeof directValue === "number"
    ? directValue
    : fallback;
};

const mapLookupOptions = (options: LookupOption[]) =>
  options.map((option) => ({
    value: option.id,
    label: option.name ?? option.code ?? String(option.id),
  }));

const WarehouseResourceModal = ({
  open,
  resource,
  record,
  parentPayload = {},
  onClose,
}: WarehouseResourceModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<ResourceFormValues>();
  const { selectedDistributor } = useSelectDistributor();
  const config = hierarchyConfig[resource];
  const fieldConfig = formFieldConfig[resource];
  const isEditing = Boolean(record);
  const distributorId = getOptionalDistributorId(selectedDistributor);
  const createMutation = useMutationCreateWarehouseResource(resource);
  const updateMutation = useMutationUpdateWarehouseResource(resource);
  const selectedWarehouseId = Form.useWatch("warehouseId", form);
  const selectedAreaId = Form.useWatch("areaId", form);
  const selectedLaneId = Form.useWatch("laneId", form);
  const warehouseTypesQuery = useQueryGetWarehouseTypes(
    open && resource === "warehouse",
  );
  const warehousesQuery = useQueryGetWarehouseLookup(
    distributorId ? { idDistributor: distributorId } : {},
    open && resource !== "warehouse",
  );
  const areasQuery = useQueryGetWarehouseChildLookup(
    "area",
    selectedWarehouseId,
    open && ["lane", "shelf", "position"].includes(resource),
  );
  const lanesQuery = useQueryGetWarehouseChildLookup(
    "lane",
    selectedAreaId,
    open && ["shelf", "position"].includes(resource),
  );
  const shelvesQuery = useQueryGetWarehouseChildLookup(
    "shelf",
    selectedLaneId,
    open && resource === "position",
  );
  const warehouseTypeOptions = useMemo(
    () =>
      getLookupOptions(warehouseTypesQuery.data?.data).map((option) => ({
        value: option.id,
        label: option.name ?? option.code ?? String(option.id),
      })),
    [warehouseTypesQuery.data?.data],
  );
  const warehouseOptions = useMemo(
    () => mapLookupOptions(getLookupOptions(warehousesQuery.data?.data)),
    [warehousesQuery.data?.data],
  );
  const areaOptions = useMemo(
    () => mapLookupOptions(getLookupOptions(areasQuery.data?.data)),
    [areasQuery.data?.data],
  );
  const laneOptions = useMemo(
    () => mapLookupOptions(getLookupOptions(lanesQuery.data?.data)),
    [lanesQuery.data?.data],
  );
  const shelfOptions = useMemo(
    () => mapLookupOptions(getLookupOptions(shelvesQuery.data?.data)),
    [shelvesQuery.data?.data],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const values: Partial<ResourceFormValues> = {
      name: record?.name ?? "",
      code: record?.code ?? "",
      description: record?.description ?? "",
      active: record ? isResourceActive(record) : true,
      warehouseTypeId: record?.warehouseType?.id,
      warehouseId: undefined,
      areaId: undefined,
      laneId: undefined,
      shelfId: undefined,
    };

    if (record) {
      values.warehouseId = getRelationId(
        record,
        "warehouse",
        parentPayload.warehouseId,
      );
      values.areaId = getRelationId(record, "area", parentPayload.areaId);
      values.laneId = getRelationId(record, "lane", parentPayload.laneId);
      values.shelfId = getRelationId(record, "shelf", parentPayload.shelfId);
    }

    form.setFieldsValue(values);
  }, [form, open, parentPayload, record]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload: WarehouseResourcePayload = {
      ...(record ? { id: record.id } : {}),
      name: values.name.trim(),
      ...(resource !== "warehouse"
        ? { code: values.code?.trim() ?? "" }
        : {}),
      description: values.description?.trim() ?? "",
      active: values.active,
      ...(distributorId ? { idDistributor: distributorId } : {}),
      ...(resource === "warehouse"
        ? { warehouseTypeId: values.warehouseTypeId }
        : {}),
      ...(resource !== "warehouse"
        ? { warehouseId: values.warehouseId }
        : {}),
      ...(resource !== "warehouse" && resource !== "area"
        ? { areaId: values.areaId }
        : {}),
      ...(resource === "shelf" || resource === "position"
        ? { laneId: values.laneId }
        : {}),
      ...(resource === "position" ? { shelfId: values.shelfId } : {}),
    };

    try {
      if (isEditing) {
        await updateMutation.mutateAsync(payload);
      } else {
        await createMutation.mutateAsync(payload);
      }

      Notification.success({
        message: t(
          isEditing
            ? "warehouse_resource_updated"
            : "warehouse_resource_created",
          { resource: t(config.singularKey) },
        ),
      });
      form.resetFields();
      onClose();
    } catch (error: unknown) {
      const responseMessage =
        error instanceof Error
          ? (error as any)?.response?.data?.message
          : undefined;

      if (!responseMessage) {
        Notification.error({
          message: t("error"),
          description: t(
            isEditing
              ? "warehouse_resource_update_failed"
              : "warehouse_resource_create_failed",
          ),
        });
      }
    }
  };

  const loading = createMutation.isPending || updateMutation.isPending;
  const showWarehouseField = resource !== "warehouse";
  const showAreaField = ["lane", "shelf", "position"].includes(resource);
  const showLaneField = ["shelf", "position"].includes(resource);
  const showShelfField = resource === "position";
  const handleWarehouseChange = () => {
    form.setFieldsValue({
      areaId: undefined,
      laneId: undefined,
      shelfId: undefined,
    });
  };
  const handleAreaChange = () => {
    form.setFieldsValue({
      laneId: undefined,
      shelfId: undefined,
    });
  };
  const handleLaneChange = () => {
    form.setFieldsValue({
      shelfId: undefined,
    });
  };

  return (
    <CustomModal
      open={open}
      title={t(isEditing ? config.editKey : config.addKey)}
      okText={t(isEditing ? "update" : "add")}
      cancelText={t("cancel")}
      onOk={handleSubmit}
      onCancel={onClose}
      okBtnLoading={loading}
      destroyOnHidden
      width={620}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className="grid grid-cols-1 pt-4 gap-x-4 sm:grid-cols-2"
      >
        {showWarehouseField && (
          <Form.Item
            name="warehouseId"
            label={t("warehouse")}
            rules={[{ required: true, message: t("") }]}
            className="mb-3!"
          >
            <CustomSelect
              options={warehouseOptions}
              placeholder={t("select_warehouse")}
              loading={warehousesQuery.isLoading}
              showSearch
              onChange={handleWarehouseChange}
              disabled={warehousesQuery.isLoading}
            />
          </Form.Item>
        )}

        {showAreaField && (
          <Form.Item
            name="areaId"
            label={t("area")}
            rules={[{ required: true, message: t("") }]}
            className="mb-3!"
          >
            <CustomSelect
              options={areaOptions}
              placeholder={t("select_area")}
              loading={areasQuery.isLoading}
              showSearch
              onChange={handleAreaChange}
              disabled={!selectedWarehouseId || areasQuery.isLoading}
            />
          </Form.Item>
        )}

        {showLaneField && (
          <Form.Item
            name="laneId"
            label={t("lane")}
            rules={[{ required: true, message: t("") }]}
            className="mb-3!"
          >
            <CustomSelect
              options={laneOptions}
              placeholder={t("select_lane")}
              loading={lanesQuery.isLoading}
              showSearch
              onChange={handleLaneChange}
              disabled={!selectedAreaId || lanesQuery.isLoading}
            />
          </Form.Item>
        )}

        {showShelfField && (
          <Form.Item
            name="shelfId"
            label={t("shelf")}
            rules={[{ required: true, message: t("") }]}
            className="mb-3!"
          >
            <CustomSelect
              options={shelfOptions}
              placeholder={t("select_shelf")}
              loading={shelvesQuery.isLoading}
              showSearch
              disabled={!selectedLaneId || shelvesQuery.isLoading}
            />
          </Form.Item>
        )}

        {resource === "warehouse" && (
          <Form.Item
            name="warehouseTypeId"
            label={t("warehouse_type")}
            rules={[{ required: true, message: t("") }]}
            className="mb-3!"
          >
            <CustomSelect
              options={warehouseTypeOptions}
              placeholder={t("select_warehouse_type")}
              loading={warehouseTypesQuery.isLoading}
              showSearch
              disabled={warehouseTypesQuery.isLoading}
            />
          </Form.Item>
        )}

        {resource !== "warehouse" && (
          <Form.Item
            name="code"
            label={t(fieldConfig.codeLabelKey ?? "code")}
            rules={[{ required: true, message: t("") }]}
            className="mb-3!"
          >
            <CustomInput
              maxLength={100}
              placeholder={t(fieldConfig.codePlaceholderKey ?? "enter_code")}
            />
          </Form.Item>
        )}

        <Form.Item
          name="name"
          label={t(fieldConfig.nameLabelKey)}
          rules={[
            { required: true, whitespace: true, message: t("") },
          ]}
          className="mb-3!"
        >
          <CustomInput
            maxLength={150}
            placeholder={t(fieldConfig.namePlaceholderKey)}
          />
        </Form.Item>

        <Form.Item
          name="active"
          label={t("status")}
          className="mb-3!"
        >
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            className="warehouse-status-control"
          >
            <Radio.Button value>{t("active")}</Radio.Button>
            <Radio.Button value={false}>{t("inactive")}</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="description"
          label={t("description")}
          className="sm:col-span-2 mb-3!"
        >
          <Input.TextArea
            rows={4}
            placeholder={t("enter_description")}
            className="px-2 py-4 custom-input no-resize"

          />
        </Form.Item>

        <Form.Item className="sm:col-span-2 mb-0!">
          <CustomActionButtons
            onCancel={onClose}
            onOk={handleSubmit}
            loading={loading}
            disabled={warehouseTypesQuery.isLoading}
            okProps={{ htmlType: "button" }}
            cancelBtnLabel="cancel"
            okBtnLabel={isEditing ? "update" : "add"}
          />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default WarehouseResourceModal;
